/**
 * HistoryPage
 *
 * Standalone page for the user's full call history (external Twilio
 * calls + internal team voice/video calls, merged + sorted by recency).
 *
 * Lifted out of CallsPage when the calls page was restructured to have
 * three top-level tabs (External Calls / Internal Calls / Video Calls);
 * History is now a separate route reached from the History button at
 * the top of /calls.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMusicPlayer } from '../context/MusicPlayerContext';
import { useVideoCall } from '../context/VideoCallContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { parseServerDate } from '../utils/dateUtils';
import DiarizedTranscript from '../components/DiarizedTranscript';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { usePageHeader } from '../context/PageHeaderContext';
import {
  Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, PhoneCall,
  FileText, Play, User, ChevronDown, ChevronUp, ArrowLeft,
} from 'lucide-react';

// US state → IANA timezone for rendering call timestamps in the
// logged-in user's home-state TZ.
const STATE_TIMEZONES = {
  AL: 'America/Chicago',     AK: 'America/Anchorage',  AZ: 'America/Phoenix',
  AR: 'America/Chicago',     CA: 'America/Los_Angeles', CO: 'America/Denver',
  CT: 'America/New_York',    DE: 'America/New_York',   FL: 'America/New_York',
  GA: 'America/New_York',    HI: 'Pacific/Honolulu',   ID: 'America/Boise',
  IL: 'America/Chicago',     IN: 'America/Indiana/Indianapolis',
  IA: 'America/Chicago',     KS: 'America/Chicago',    KY: 'America/New_York',
  LA: 'America/Chicago',     ME: 'America/New_York',   MD: 'America/New_York',
  MA: 'America/New_York',    MI: 'America/Detroit',    MN: 'America/Chicago',
  MS: 'America/Chicago',     MO: 'America/Chicago',    MT: 'America/Denver',
  NE: 'America/Chicago',     NV: 'America/Los_Angeles', NH: 'America/New_York',
  NJ: 'America/New_York',    NM: 'America/Denver',     NY: 'America/New_York',
  NC: 'America/New_York',    ND: 'America/Chicago',    OH: 'America/New_York',
  OK: 'America/Chicago',     OR: 'America/Los_Angeles', PA: 'America/New_York',
  RI: 'America/New_York',    SC: 'America/New_York',   SD: 'America/Chicago',
  TN: 'America/Chicago',     TX: 'America/Chicago',    UT: 'America/Denver',
  VT: 'America/New_York',    VA: 'America/New_York',   WA: 'America/Los_Angeles',
  WV: 'America/New_York',    WI: 'America/Chicago',    WY: 'America/Denver',
  DC: 'America/New_York',
};

const formatCallTimestamp = (raw, stateAbbr) => {
  if (!raw) return '';
  const d = parseServerDate(raw);
  if (!d || isNaN(d.getTime())) return '';
  const tz = STATE_TIMEZONES[(stateAbbr || '').toUpperCase()] || undefined;
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
      timeZone: tz,
      timeZoneName: 'short',
    }).format(d);
  } catch (e) {
    return d.toLocaleString();
  }
};

const formatDuration = (seconds) => {
  const s = Number(seconds) || 0;
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const getCallIcon = (type) => {
  switch (type) {
    case 'inbound':  return <PhoneIncoming className="w-4 h-4 text-green-500" />;
    case 'outbound': return <PhoneOutgoing className="w-4 h-4 text-primary" />;
    case 'missed':   return <PhoneMissed className="w-4 h-4 text-red-500" />;
    default:         return <Phone className="w-4 h-4" />;
  }
};

const HistoryPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, user } = useAuth();
  const { playSong } = useMusicPlayer();
  const { initiateCall } = useVideoCall();

  const [callHistory, setCallHistory]           = useState([]);
  const [videoCallHistory, setVideoCallHistory] = useState([]);
  const [voicemailsBySid, setVoicemailsBySid]   = useState({});
  const [callTypeFilter, setCallTypeFilter]     = useState(null);
  const [expandedCallId, setExpandedCallId]     = useState(null);
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [showTranscriptDialog, setShowTranscriptDialog] = useState(false);
  const deepLinkedOnceRef = useRef(false);
  const { currentSong, isPlaying } = useMusicPlayer();

  // Mobile header — title "Call History" + back arrow to /calls.
  usePageHeader({ title: 'Call History', back: '/calls' }, []);

  useEffect(() => {
    api.get('/calls').then((r) => setCallHistory(r.data || [])).catch(() => {});
    api.get('/video-calls/history/list').then((r) => setVideoCallHistory(r.data?.calls || [])).catch(() => {});
    // Voicemails are joined to missed external calls via
    // `external_call_sid` so we can conditionally show the Play /
    // Transcript buttons on a missed-call row only when a VM
    // actually exists for it.
    api.get('/voicemail/inbox').then((r) => {
      const vms = r.data?.items || [];
      const bySid = {};
      vms.forEach((vm) => {
        if (vm.external_call_sid) bySid[vm.external_call_sid] = vm;
      });
      setVoicemailsBySid(bySid);
    }).catch(() => {});
  }, [api]);

  // Deep-link: `?call=<id>` auto-expands the matching card and scrolls
  // it into view. Fired from missed-call push / in-app notifications
  // so a tap lands the user directly on the card with summary +
  // transcript + play-recording exposed (no Call Back).
  //
  // iter122 — IDs are now prefixed `external-` / `internal-` to
  // guarantee uniqueness in the rendered list. The query-string
  // value may be the raw id OR the twilio_call_sid (older notifs),
  // so match against any of those plus the prefixed form.
  useEffect(() => {
    const callId = searchParams.get('call');
    if (!callId || deepLinkedOnceRef.current) return;
    if (callHistory.length === 0 && videoCallHistory.length === 0) return;
    const matchRaw = (raw) => raw === callId
      || raw === `external-${callId}`
      || raw === `internal-${callId}`;
    const externalMatch = (callHistory || []).find((c) =>
      matchRaw(c.id || '') || matchRaw(c.twilio_call_sid || '')
    );
    const internalMatch = (videoCallHistory || []).find((c) => matchRaw(c.id || ''));
    const prefixedId = externalMatch
      ? `external-${externalMatch.id || externalMatch.twilio_call_sid}`
      : (internalMatch ? `internal-${internalMatch.id}` : null);
    if (!prefixedId) return;
    deepLinkedOnceRef.current = true;
    setExpandedCallId(prefixedId);
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="call-log-${prefixedId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 200);
  }, [searchParams, callHistory, videoCallHistory]);

  const handlePlayRecording = async (call) => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    let fetchUrl = null;
    let isInternalDirect = false;
    // Missed calls play the VOICEMAIL (not the empty call recording)
    // when one was left. The `voicemail` field is injected during row
    // merge below from the voicemailsBySid map.
    if (call.voicemail) {
      fetchUrl = `${apiBase}/api/voicemail/${call.voicemail.id}/audio`;
    } else if (call._kind === 'internal') {
      const ref = call.original?.recording_url || call.recording_url;
      if (ref && ref !== '__INTERNAL__') {
        fetchUrl = ref.startsWith('http') ? ref : `${apiBase}${ref}`;
        isInternalDirect = true;
      }
    } else if (call.recording_url) {
      fetchUrl = `${apiBase}/api/twilio/recording/${call.id}/audio`;
    }
    if (!fetchUrl) {
      toast.message('Recording not available yet — it usually appears within a minute of hangup.');
      return;
    }
    // Toggle: if THIS recording is already loaded, calling playSong
    // with the same `id` tells MusicPlayerContext to pause/resume
    // instead of re-fetching.
    const audioId = call.voicemail ? `voicemail-${call.voicemail.id}` : `recording-${call.id}`;
    if (currentSong?.id === audioId) {
      playSong({ id: audioId }, 'recording'); // ctx toggles play/pause by id
      return;
    }
    try {
      const headers = isInternalDirect
        ? {}
        : { Authorization: `Bearer ${localStorage.getItem('orb_token') || ''}` };
      const resp = await fetch(fetchUrl, { headers });
      if (!resp.ok) {
        toast.error(`Recording failed to load (HTTP ${resp.status})`);
        return;
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      playSong({
        id: audioId,
        title: call.voicemail ? 'Voicemail' : 'Call Recording',
        subtitle: call.phone_number,
        artist: (call.call_type || '').charAt(0).toUpperCase() + (call.call_type || '').slice(1),
        url: blobUrl,
      }, 'recording');
    } catch (e) {
      toast.error(e?.message || 'Could not load recording');
    }
  };

  const handleShowTranscript = (call) => {
    // Pass the voicemail transcript when this is a missed call with
    // a VM; otherwise use the call transcript (external or internal).
    const payload = call.voicemail
      ? { ...call, transcript: call.voicemail.transcript || null }
      : call;
    setSelectedTranscript(payload);
    setShowTranscriptDialog(true);
  };

  // Merge external Twilio call rows with internal team-call rows so
  // the History page is a single source of truth, sorted newest first.
  //
  // iter122 — keys are scoped by `_kind` so a hypothetical UUID
  // collision between an external Twilio row id and an internal video
  // call id can't ever produce duplicate React keys (which would
  // cause "click row N → row N+1 expands" rendering bugs).
  const internalRows = (videoCallHistory || []).map((c) => {
    const isOutbound = c.initiated_by_user_id === user?.id;
    const otherIdx = (c.participant_user_ids || []).findIndex((id) => id !== user?.id);
    const otherName = (c.participant_names || [])[otherIdx] || c.initiated_by_name || 'Team member';
    const callType = c.was_missed ? 'missed'
      : c.was_declined ? 'declined'
      : isOutbound ? 'outbound' : 'inbound';
    return {
      id: `internal-${c.id}`,
      _kind: 'internal',
      _video: c.call_mode === 'video',
      original: c,
      phone_number: otherName,
      call_type: callType,
      duration: c.duration_seconds || 0,
      created_at: c.created_at,
      ai_summary: c.ai_summary,
      transcript: c.transcript || (c.transcript_available ? '__INTERNAL__' : null),
      recording_url: c.recording_url || (c.recording_available ? '__INTERNAL__' : null),
    };
  });
  const externalRows = (callHistory || []).map((c) => {
    // The /api/calls endpoint sets `id` to the twilio_call_sid when
    // present, so we can join on that to our voicemail map.
    const sid = c.id || c.twilio_call_sid;
    const vm = sid ? voicemailsBySid[sid] : null;
    return {
      ...c,
      id: `external-${c.id || c.twilio_call_sid || Math.random().toString(36).slice(2)}`,
      _kind: 'external',
      // When a VM was left, the Play button plays the VM audio and
      // the Transcript button shows the VM transcript — not the
      // (likely-empty) call recording / transcript.
      voicemail: vm || null,
      twilio_call_sid: sid,
    };
  });
  const merged = [...internalRows, ...externalRows].sort(
    (a, b) => (parseServerDate(b.created_at)?.getTime() || 0) - (parseServerDate(a.created_at)?.getTime() || 0)
  );
  const filteredCalls = callTypeFilter
    ? merged.filter((c) => c.call_type === callTypeFilter)
    : merged;

  return (
    <div
      className="fixed inset-x-0 overflow-y-auto bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-3 space-y-3"
      style={{
        top: 'calc(var(--app-header-h) + env(safe-area-inset-top, 0px))',
        bottom: 'calc(var(--app-bottom-h, 4rem) + env(safe-area-inset-bottom, 0px))',
      }}
      data-testid="history-page"
    >
      {/* Filter Toggle */}
      <div className="flex gap-1.5">
        <Button
          variant={callTypeFilter === null ? 'default' : 'outline'}
          size="sm"
          className={`flex-1 h-8 text-xs ${callTypeFilter === null ? 'bg-[#586D52] hover:bg-[#4a5d46]' : ''}`}
          onClick={() => setCallTypeFilter(null)}
          data-testid="filter-all"
        >
          All
        </Button>
        <Button
          variant={callTypeFilter === 'inbound' ? 'default' : 'outline'}
          size="sm"
          className={`flex-1 h-8 text-xs ${callTypeFilter === 'inbound' ? 'bg-green-600 hover:bg-green-700' : ''}`}
          onClick={() => setCallTypeFilter(callTypeFilter === 'inbound' ? null : 'inbound')}
          data-testid="filter-incoming"
        >
          <PhoneIncoming className="w-3.5 h-3.5 mr-1" />
          Incoming
        </Button>
        <Button
          variant={callTypeFilter === 'outbound' ? 'default' : 'outline'}
          size="sm"
          className={`flex-1 h-8 text-xs ${callTypeFilter === 'outbound' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
          onClick={() => setCallTypeFilter(callTypeFilter === 'outbound' ? null : 'outbound')}
          data-testid="filter-outgoing"
        >
          <PhoneOutgoing className="w-3.5 h-3.5 mr-1" />
          Outgoing
        </Button>
        <Button
          variant={callTypeFilter === 'missed' ? 'default' : 'outline'}
          size="sm"
          className={`flex-1 h-8 text-xs ${callTypeFilter === 'missed' ? 'bg-red-600 hover:bg-red-700' : ''}`}
          onClick={() => setCallTypeFilter(callTypeFilter === 'missed' ? null : 'missed')}
          data-testid="filter-missed"
        >
          <PhoneMissed className="w-3.5 h-3.5 mr-1" />
          Missed
        </Button>
      </div>

      {filteredCalls.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Phone className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p>{callTypeFilter ? `No ${callTypeFilter} calls` : 'No call history yet'}</p>
        </div>
      ) : (
        <div className="space-y-1.5 pb-20">
          {filteredCalls.map((call) => {
            const isExpanded = expandedCallId === call.id;
            return (
              <Card
                key={call.id}
                className="bg-white dark:bg-[#1a1a1a] border-0 shadow-sm overflow-hidden"
                data-testid={`call-log-${call.id}`}
              >
                <div
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  onClick={() => setExpandedCallId(isExpanded ? null : call.id)}
                  data-testid={`call-header-${call.id}`}
                >
                  {getCallIcon(call.call_type)}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{call.phone_number}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatCallTimestamp(call.created_at, user?.state)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right">
                      <p className="text-xs">{formatDuration(call.duration)}</p>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {call._kind === 'internal' ? (call._video ? 'video' : 'voice') : call.call_type}
                      </Badge>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <CardContent className="px-3 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800">
                    <div className="space-y-3 mt-2">
                      <div className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg">
                        <User className="w-8 h-8 p-1.5 bg-muted rounded-full" />
                        <div>
                          <p className="font-medium text-sm">{call.phone_number}</p>
                          <p className="text-xs text-muted-foreground">
                            {(call.call_type || '').charAt(0).toUpperCase() + (call.call_type || '').slice(1)} • {formatDuration(call.duration)}
                          </p>
                        </div>
                      </div>

                      <div>
                        <Label className="text-xs text-muted-foreground uppercase">AI Summary</Label>
                        {call.ai_summary ? (
                          <p className="text-sm bg-muted/30 p-2 rounded-lg mt-1">{call.ai_summary}</p>
                        ) : (
                          <p className="text-xs italic text-muted-foreground bg-muted/20 p-2 rounded-lg mt-1">
                            Call summary will appear here once the recording finishes processing.
                          </p>
                        )}
                      </div>

                      {(() => {
                        // Per-call-type button visibility:
                        //  • missed → ONLY show Play / Transcript when
                        //    a voicemail was actually left (and the
                        //    Transcript button additionally requires
                        //    the VM transcript to exist).
                        //  • inbound/outbound → always show both.
                        //  • Play button is a true toggle: shows a
                        //    Pause icon while this exact recording is
                        //    currently playing.
                        const isMissed = call.call_type === 'missed';
                        const hasVm = Boolean(call.voicemail);
                        const canShowPlay = isMissed ? hasVm : true;
                        const canShowTranscript = isMissed
                          ? Boolean(call.voicemail?.transcript)
                          : true;
                        const myAudioId = call.voicemail
                          ? `voicemail-${call.voicemail.id}`
                          : `recording-${call.id}`;
                        const amPlaying = isPlaying && currentSong?.id === myAudioId;

                        if (!canShowPlay && !canShowTranscript) return null;

                        return (
                          <div className="flex gap-2">
                            {canShowPlay && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => {
                                  if (!isMissed) {
                                    // Non-missed with no recording info yet
                                    // (post-call pipeline hasn't finished)
                                    // falls back to a soft notice.
                                    if (!call.recording_url && call._kind !== 'internal') {
                                      toast.message('Recording not ready yet — check back in a minute.');
                                      return;
                                    }
                                  }
                                  handlePlayRecording(call);
                                }}
                                data-testid={`play-recording-${call.id}`}
                              >
                                {amPlaying ? (
                                  <>
                                    <span className="inline-block w-3.5 h-3.5 mr-1.5 align-middle">
                                      {/* Simple pause icon */}
                                      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                        <rect x="6" y="5" width="4" height="14" rx="1"/>
                                        <rect x="14" y="5" width="4" height="14" rx="1"/>
                                      </svg>
                                    </span>
                                    Pause
                                  </>
                                ) : (
                                  <>
                                    <Play className="w-3.5 h-3.5 mr-1.5" />
                                    {isMissed ? 'Play Voicemail' : 'Play Recording'}
                                  </>
                                )}
                              </Button>
                            )}
                            {canShowTranscript && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => {
                                  const text = isMissed
                                    ? call.voicemail?.transcript
                                    : call.transcript;
                                  if (!text && !isMissed) {
                                    toast.message('Transcript still processing — usually ready within a minute of hangup.');
                                    return;
                                  }
                                  handleShowTranscript(call);
                                }}
                                data-testid={`view-transcript-${call.id}`}
                              >
                                <FileText className="w-3.5 h-3.5 mr-1.5" />
                                Transcript
                              </Button>
                            )}
                          </div>
                        );
                      })()}

                      <Button
                        size="sm"
                        className="w-full bg-[#586D52] hover:bg-[#4a5d46]"
                        onClick={async () => {
                          if (call._kind === 'internal') {
                            try {
                              const otherIdx = (call.original?.participant_user_ids || []).findIndex((id) => id !== user?.id);
                              const calleeId = (call.original?.participant_user_ids || [])[otherIdx];
                              if (!calleeId) {
                                toast.error('Could not determine who to call back');
                                return;
                              }
                              await initiateCall(calleeId, call._video ? 'video' : 'voice');
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || 'Failed to call back');
                            }
                          } else {
                            navigate(`/calls?tab=external`);
                            toast.info(`Open dialer to call ${call.phone_number}`);
                          }
                        }}
                        data-testid={`call-back-${call.id}`}
                      >
                        <PhoneCall className="w-3.5 h-3.5 mr-1.5" />
                        Call Back
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Transcript Dialog */}
      <Dialog open={showTranscriptDialog} onOpenChange={setShowTranscriptDialog}>
        <DialogContent className="max-w-sm w-[calc(100%-2rem)] p-0 overflow-hidden bg-white dark:bg-[#1a1a1a] rounded-3xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <DialogHeader className="p-4 pb-2">
            <DialogTitle className="text-lg font-bold">Call Transcript</DialogTitle>
            {selectedTranscript && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {selectedTranscript.phone_number} • {formatDuration(selectedTranscript.duration)}
              </p>
            )}
          </DialogHeader>
          <div className="px-4 pb-4">
            {selectedTranscript && (
              <ScrollArea className="h-[40vh] bg-muted/30 p-3 rounded-xl">
                <DiarizedTranscript
                  segments={selectedTranscript.diarized_segments}
                  fallbackText={selectedTranscript.transcript}
                />
              </ScrollArea>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HistoryPage;
